import {
  useListAssignableUsers,
  useListWorkspacePeople,
} from '@/lib/api/generated/api';
import type { UserSummary } from '@/lib/api/generated/model';

/**
 * Everyone who can be assigned to a Human Gate.
 *
 * Asked for when the builder opens a step, which is what makes a colleague
 * provisioned five minutes ago selectable now — the backend reconciles from the
 * identity provider on this call rather than waiting for them to sign in
 * (spec 067 FR-013, SC-003).
 *
 * An empty list on failure, not an error: a picker that cannot list people is a
 * picker with nobody in it, and blocking the whole step panel because the
 * directory is briefly unreachable would be a worse trade than showing the
 * assignment control unpopulated.
 */
export interface AssignablePeople {
  people: UserSummary[];
  /**
   * True when the directory could not be read at all.
   *
   * Kept separate from an empty list because the two mean opposite things to
   * whoever is looking at the picker: "nobody has been provisioned yet" is a fact
   * about this realm, and "the directory is unreachable" is a fact about right
   * now. Collapsing both into no options left a picker that said "no one matches"
   * during an outage, which is a confident answer to a question it could not
   * actually answer.
   */
  failed: boolean;
  loading: boolean;
}

export function useAssignablePeople(
  workspaceId?: string,
  /** `false` reads nothing, for a caller that needs the people only sometimes. */
  { enabled = true }: { enabled?: boolean } = {},
): AssignablePeople {
  // With a workspace, its people (spec 186): once workspace access is on, the
  // service answers with its owners and members only, so a gate or a mention
  // never offers someone who cannot see the work. Without one, everyone, as
  // before — the add-member picker is the one caller that needs the whole
  // organization.
  const everyone = useListAssignableUsers(undefined, {
    query: { staleTime: 60_000, enabled: enabled && workspaceId === undefined },
  });
  const inWorkspace = useListWorkspacePeople(workspaceId ?? '', undefined, {
    query: { staleTime: 60_000, enabled: enabled && workspaceId !== undefined },
  });
  const { data, isPending, isError } =
    workspaceId === undefined ? everyone : inWorkspace;
  const ok = data?.status === 200;
  return {
    people: ok ? data.data.items : [],
    // A non-200 body counts as a failure too: the generated client resolves rather
    // than throws on 4xx/5xx, so `isError` alone misses a 403 or a 500.
    failed: isError || (!isPending && !ok),
    loading: isPending,
  };
}
